import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "Layout/slice";
import { trackEvent } from "utils/tracking";
import { TRADE_SIDE } from "utils/constants";
import { resolveMinimumInvestment } from "utils/index";
import { useAllStrategyHoldings } from "features/Strategy/hook/useAllStrategyHoldings";
import {
  useBrokerAccounts,
  useTradingConnection,
  useTradingAllocation,
  usePostTradingConnection,
  brokerAccountsQueryKey,
} from "./index";

const SELL_ALL_EPSILON = 0.005;

export const useInvestmentOrder = (strategyId, minimumInvestment) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Local state
  const [activeTab, setActiveTab] = useState(TRADE_SIDE.BUY);
  const [step, setStep] = useState(1);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [strategyData, setStrategyData] = useState(null);
  const [tradingConnectionData, setTradingConnectionData] = useState(null);
  const [isLoadingCash, setIsLoadingCash] = useState(false);
  const [isHoldingsModalOpen, setIsHoldingsModalOpen] = useState(false);

  // Fetch strategy holdings data
  const {
    data: holdingsData = [],
    isLoading: isLoadingHoldings,
    isError: isHoldingsError,
  } = useAllStrategyHoldings(strategyId);

  // Fetch broker accounts using custom hook
  const {
    data: portfolios = [],
    isLoading: loading,
    isError,
  } = useBrokerAccounts(
    strategyId,
    (response) => {
      // Extract strategy data from response
      if (response && Array.isArray(response)) {
        const firstItem = response[0];
        if (firstItem && firstItem.strategy_name) {
          setStrategyData({
            strategy_name: firstItem.strategy_name,
            logo_url: firstItem.logo_url,
            strategy_id: firstItem.strategy_id,
          });
        }
      }
    },
    () => {
      dispatch(
        toast({
          type: "error",
          body: "Failed to load broker accounts. Please try again.",
          visible: true,
        })
      );
    }
  );

  // Derived from the latest broker-accounts payload so a strategy switch or
  // background refetch can't leave a stale snapshot driving the order math.
  const selectedAccount = useMemo(
    () =>
      portfolios?.find((a) => a.broker_account_id === selectedAccountId) || "",
    [portfolios, selectedAccountId]
  );

  // Create trading connection mutation
  const createTradingConnectionMutation = useTradingConnection(
    (response) => {
      // Store the trading connection data
      if (response) {
        setTradingConnectionData(response);
      }
      setIsLoadingCash(false);
      setIsSubmitting(false);
    },
    (error) => {
      // Clear trading connection data on error
      setTradingConnectionData(null);

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create trading connection. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsLoadingCash(false);
      setIsSubmitting(false);
    }
  );

  // Post trading connection mutation (for not connected accounts)
  const postTradingConnectionMutation = usePostTradingConnection(
    async (response) => {
      // Check if the response indicates failure despite 200 status
      if (response && response.success === false) {
        // Handle as error even though it's a 200 response
        dispatch(
          toast({
            type: "error",
            body:
              response.message || "Failed to submit order. Please try again.",
            visible: true,
          })
        );
        setIsSubmitting(false);
        return;
      }

      // Refetch the invested snapshot so a follow-up order in the same
      // session is based on the post-order value, not the cached one.
      await queryClient.invalidateQueries({
        queryKey: brokerAccountsQueryKey(strategyId),
        refetchType: "all",
      });
      setIsSubmitting(false);
      setOrderSubmitted(true);
    },
    (error) => {
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit order. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsSubmitting(false);
    }
  );

  // Update trading allocation mutation (for active accounts)
  const updateTradingAllocationMutation = useTradingAllocation(
    async (response) => {
      // Check if the response indicates failure despite 200 status
      if (response && response.success === false) {
        // Handle as error even though it's a 200 response
        dispatch(
          toast({
            type: "error",
            body:
              response.message || "Failed to submit order. Please try again.",
            visible: true,
          })
        );
        setIsSubmitting(false);
        return;
      }

      // Refetch the invested snapshot so a follow-up order in the same
      // session is based on the post-order value, not the cached one.
      await queryClient.invalidateQueries({
        queryKey: brokerAccountsQueryKey(strategyId),
        refetchType: "all",
      });
      setIsSubmitting(false);
      setOrderSubmitted(true);
    },
    (error) => {
      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit order. Please try again.";

      dispatch(
        toast({
          type: "error",
          body: errorMessage,
          visible: true,
        })
      );
      setIsSubmitting(false);
    }
  );

  // Computed values
  const availableCash =
    tradingConnectionData?.available_funds_for_trading?.raw_number;
  const availableCashFormatted =
    tradingConnectionData?.available_funds_for_trading?.formatted;
  const accountValue = tradingConnectionData?.account_value?.raw_number;
  const strategy = strategyData?.strategy_name || "Strategy";
  const investedSnapshot = selectedAccount?.invested?.raw_number ?? 0;

  const additionalCash =
    investmentAmount && !isNaN(investmentAmount)
      ? (parseFloat(investmentAmount) - availableCash).toFixed(2)
      : 0;

  const showAdditionalCashAlert =
    activeTab === TRADE_SIDE.BUY &&
    selectedAccount &&
    investmentAmount &&
    parseFloat(investmentAmount) > availableCash;

  // A strategy without a trading connection on the selected account yet gets
  // one created via postTradingConnection (trading-connection/create/) — the
  // strategy's minimum_investment only applies to that first-time creation,
  // not to topping up / selling from an allocation that already exists.
  const hasExistingConnection = Boolean(
    tradingConnectionData?.trading_connections?.some(
      (connection) => connection.strategy_id === strategyId
    )
  );
  const isCreatingNewConnection =
    activeTab === TRADE_SIDE.BUY && !hasExistingConnection;
  const minimumInvestmentValue = resolveMinimumInvestment(minimumInvestment);

  const isAmountValid =
    investmentAmount &&
    !isNaN(investmentAmount) &&
    parseFloat(investmentAmount) > 0 &&
    (!isCreatingNewConnection ||
      parseFloat(investmentAmount) >= minimumInvestmentValue.raw);

  const amountErrorMessage =
    isCreatingNewConnection &&
    investmentAmount &&
    !isNaN(investmentAmount) &&
    parseFloat(investmentAmount) > 0 &&
    parseFloat(investmentAmount) < minimumInvestmentValue.raw
      ? `Please enter a value ≥ ${minimumInvestmentValue.formatted}`
      : "Enter a valid amount greater than 0.";

  // Event handlers
  const handleNext = () => {
    if (step === 1 && selectedAccount && isAmountValid) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (orderSubmitted) {
      setOrderSubmitted(false);
      setStep(1);
    }
  };

  const handleSubmitOrder = () => {
    if (!selectedAccount?.broker_account_id) {
      dispatch(
        toast({
          type: "error",
          body: "Please select an account before submitting the order.",
          visible: true,
        })
      );
      return;
    }

    setIsSubmitting(true);

    // Prepare payload for update trading allocation
    const dollarAmount = parseFloat(investmentAmount);
    //   if (activeTab === "buy" && parseFloat(investmentAmount) > availableCash) {
    //           dispatch(
    //       toast({
    //         type: "error",
    //         body:  `You cannot invest more than your available cash (${availableCashFormatted}).`,
    //         visible: true,
    //       })
    //     );
    //   setIsSubmitting(false);
    //   return;
    // }

    if (
      activeTab === TRADE_SIDE.SELL &&
      (investedSnapshot <= 0 ||
        dollarAmount > investedSnapshot + SELL_ALL_EPSILON)
    ) {
      dispatch(
        toast({
          type: "error",
          body: `You can not sell more than you have invested in this strategy. You have ${
            selectedAccount?.invested?.formatted ||
            `$${investedSnapshot.toFixed(2)}`
          }.`,
          visible: true,
        })
      );
      setIsSubmitting(false);
      return;
    }

    // A literal 0 tells the backend to liquidate and cancel the connection
    const isSellAll =
      activeTab === TRADE_SIDE.SELL &&
      dollarAmount >= investedSnapshot - SELL_ALL_EPSILON;

    // Every order against an existing connection (including sell-all, which
    // liquidates and cancels) requires a successfully loaded account.
    const needsAccountValue =
      activeTab === TRADE_SIDE.SELL || hasExistingConnection;
    if (needsAccountValue && accountValue == null) {
      dispatch(
        toast({
          type: "error",
          body: "Unable to load your account value. Please reselect your account and try again.",
          visible: true,
        })
      );
      setIsSubmitting(false);
      return;
    }

    trackEvent({
      name: "click_start_strategy",
      label: "Strategy",
      strategy_id: strategyId,
      strategy_name: strategyData?.strategy_name,
      side: activeTab,
      is_sell_all: isSellAll,
    });

    if (activeTab === TRADE_SIDE.SELL) {
      // Always use updateTradingAllocation for sell operations
      const updatePayload = {
        new_allocation: {
          [strategyId]: isSellAll
            ? 0
            : (investedSnapshot - dollarAmount) / accountValue,
        },
        update_flag: {
          [strategyId]: true,
        },
      };
      updateTradingAllocationMutation.mutate({
        brokerAccountId: selectedAccount.broker_account_id,
        payload: updatePayload,
      });
    } else {
      // For buy operations, check if strategy has existing connection
      if (hasExistingConnection) {
        // If strategy has existing connection, use updateTradingAllocation
        const updatePayload = {
          new_allocation: {
            [strategyId]: (investedSnapshot + dollarAmount) / accountValue,
          },
          update_flag: {
            [strategyId]: true,
          },
        };
        updateTradingAllocationMutation.mutate({
          brokerAccountId: selectedAccount.broker_account_id,
          payload: updatePayload,
        });
      } else {
        // If no existing connection, use postTradingConnection
        const postPayload = {
          account_id: selectedAccount.broker_account_id,
          strategy: strategyId,
          dollar_allocation: dollarAmount,
        };
        postTradingConnectionMutation.mutate(postPayload);
      }
    }
  };

  const handleDone = () => {
    setOrderSubmitted(false);
    setStep(1);
    setInvestmentAmount("");
    setSelectedAccountId("");
  };

  const handleViewDetails = () => {
    setIsHoldingsModalOpen(true);
  };

  const handleAccountSelect = (val) => {
    const account = portfolios?.find((a) => a.broker_account_id === val);

    if (account) {
      setSelectedAccountId(val);

      // Drop the previous account's payload so the order guards can't pass
      // on its account value while this account's fetch is in flight.
      setTradingConnectionData(null);
      setIsLoadingCash(true);
      createTradingConnectionMutation.mutate(account.broker_account_id);
    }
  };
  const handleSellAll = () => {
    if (investedSnapshot > 0) {
      setInvestmentAmount(investedSnapshot);
    }
  };

  const showSellAll = useMemo(() => {
    if (activeTab !== TRADE_SIDE.SELL) return false;
    if (!selectedAccount) return false;
    if (!tradingConnectionData?.trading_connections) return false;

    return investedSnapshot > 0;
  }, [activeTab, selectedAccount, tradingConnectionData, investedSnapshot]);

  return {
    // State
    activeTab,
    step,
    selectedAccount,
    investmentAmount,
    isSubmitting,
    orderSubmitted,
    strategyData,
    tradingConnectionData,
    isLoadingCash,
    isHoldingsModalOpen,
    showSellAll,

    // API Data
    portfolios,
    loading,
    isError,
    holdingsData,
    isLoadingHoldings,
    isHoldingsError,

    // Computed values
    availableCash,
    availableCashFormatted,
    strategy,
    additionalCash,
    showAdditionalCashAlert,
    isAmountValid,
    amountErrorMessage,

    // Setters
    setActiveTab,
    setInvestmentAmount,
    setIsHoldingsModalOpen,

    // Event handlers
    handleNext,
    handleBack,
    handleSubmitOrder,
    handleDone,
    handleViewDetails,
    handleAccountSelect,
    handleSellAll,
  };
};
